import React, { useState, useEffect } from 'react';
import { Organization, OrganizationCreate, OrganizationUpdate, AccessType } from '../../types';
import { XMarkIcon, BuildingOfficeIcon, EnvelopeIcon, CreditCardIcon, ShieldCheckIcon, ClockIcon, CubeIcon, CheckIcon } from '@heroicons/react/24/outline';
import TrialManagement from '../users/TrialManagement';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { showToast } from '../../store/slices/toastSlice';
import { organizationLimitsApi } from '../../services/limitsApi';
import { appFeaturesApi, FEATURE_DEFINITIONS } from '../../services/appFeaturesApi';

interface OrganizationFormProps {
  organization?: Organization;
  onSubmit: (data: OrganizationCreate | OrganizationUpdate) => void;
  onCancel: () => void;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  organization,
  onSubmit,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isOrgAdmin = currentUser?.role === 'org_admin';
  const canManageLimits = isSuperAdmin || isOrgAdmin;

  const [formData, setFormData] = useState({
    name: organization?.name || '',
    domains: organization?.domains || [],
    billing_email: organization?.billing_email || '',
    technical_contact_email: organization?.technical_contact_email || '',
    max_users: organization?.max_users || 100,
    status: organization?.status || 'active',
    settings: {
      credit_limit: organization?.credit_limit || 1000,
      access_type: organization?.access_type || ['web'],
    },
  });

  const [domainInput, setDomainInput] = useState('');

  // Trial management state (only for super admins)
  const [trialInfo, setTrialInfo] = useState({
    web_trial: organization?.web_trial || false,
    ide_trial: organization?.ide_trial || false,
    security_trial: organization?.security_trial || false,
    devops_trial: organization?.devops_trial || false,
    web_trial_expires: organization?.web_trial_expires || null,
    ide_trial_expires: organization?.ide_trial_expires || null,
    security_trial_expires: organization?.security_trial_expires || null,
    devops_trial_expires: organization?.devops_trial_expires || null,
  });

  // Organization limits state
  const [orgLimits, setOrgLimits] = useState({
    credit_soft_limit: 10000,
    credit_hard_limit: 12000,
    credit_hard_limit_enabled: true,
    credit_pool_monthly: 15000,
    credit_pool_yearly: undefined,
    credit_pool_type: 'monthly' as 'monthly' | 'yearly',
    // For org admins, start with no feature access until API loads
    // For super admins, show all features by default
    security_access: isSuperAdmin,
    devops_access: isSuperAdmin,
    ide_access: isSuperAdmin,
    web_access: isSuperAdmin,
    auto_topup_enabled: false,
    auto_topup_amount: 5000,
    auto_topup_threshold: 90,
    notification_threshold_80: true,
    notification_threshold_90: true,
    notification_threshold_95: true,
  });

  // App Features state
  const [availableFeatures, setAvailableFeatures] = useState<string[]>([]);
  const [orgAppFeatures, setOrgAppFeatures] = useState<string[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'max_users' ? parseInt(value, 10) : value,
    }));
  };

  const handleDomainKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addDomain();
    }
  };

  const addDomain = () => {
    const domain = domainInput.trim().toLowerCase();
    if (domain && !formData.domains.includes(domain)) {
      setFormData(prev => ({
        ...prev,
        domains: [...prev.domains, domain],
      }));
      setDomainInput('');
    }
  };

  const removeDomain = (domain: string) => {
    setFormData(prev => ({
      ...prev,
      domains: prev.domains.filter(d => d !== domain),
    }));
  };

  // Fetch existing limits and app features when editing an organization
  useEffect(() => {
    const fetchData = async () => {
      if (organization) {
        // Fetch limits if user can manage them
        if (canManageLimits) {
          try {
            const limits = await organizationLimitsApi.getOrgLimits(organization.org_id);
            setOrgLimits(limits);
          } catch (error) {
            // If no limits exist (404), keep default values
            console.log('No existing limits found, using defaults');
          }
        }

        // Fetch app features if super admin
        if (isSuperAdmin) {
          setLoadingFeatures(true);
          try {
            const [availableResponse, orgFeaturesResponse] = await Promise.all([
              appFeaturesApi.getAvailableFeatures(),
              appFeaturesApi.getOrganizationFeatures(organization.org_id)
            ]);
            setAvailableFeatures(availableResponse.available_features);
            setOrgAppFeatures(orgFeaturesResponse.app_features);
          } catch (error) {
            console.error('Error loading app features:', error);
          } finally {
            setLoadingFeatures(false);
          }
        }
      }
    };

    fetchData();
  }, [organization, canManageLimits, isSuperAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (domainInput.trim()) {
      addDomain();
    }
    
    // Validation: Check if soft/hard limits are within pool amount
    if (canManageLimits && organization) {
      const poolAmount = orgLimits.credit_pool_type === 'monthly' 
        ? orgLimits.credit_pool_monthly || 0 
        : orgLimits.credit_pool_yearly || 0;
      
      if (orgLimits.credit_soft_limit > poolAmount) {
        dispatch(showToast({
          message: `Soft limit (${orgLimits.credit_soft_limit}) cannot exceed pool amount (${poolAmount})`,
          type: 'error'
        }));
        return;
      }
      
      if (orgLimits.credit_hard_limit > poolAmount) {
        dispatch(showToast({
          message: `Hard limit (${orgLimits.credit_hard_limit}) cannot exceed pool amount (${poolAmount})`,
          type: 'error'
        }));
        return;
      }
    }
    
    const submitData = {
      ...formData,
      // Only include these fields for super admin
      ...(isSuperAdmin && {
        max_users: formData.max_users,
        status: formData.status,
      }),
      credit_limit: canManageLimits ? orgLimits.credit_hard_limit : undefined,
      access_type: isSuperAdmin ? formData.settings.access_type : undefined,
      // Include app features for super admin and existing organizations
      ...(isSuperAdmin && organization && {
        app_features: orgAppFeatures,
      }),
      // Include trial data for super admin and existing organizations
      ...(isSuperAdmin && organization && {
        ...trialInfo,
      }),
      // Include limits data for admins and existing organizations
      ...(canManageLimits && organization && {
        limits: orgLimits,
      }),
      settings: {},
      members: []
    };
    
    try {
      await onSubmit(submitData);
      dispatch(showToast({
        message: organization 
          ? 'Organization updated successfully' 
          : 'Organization created successfully',
        type: 'success'
      }));
    } catch (error) {
      dispatch(showToast({
        message: 'Failed to save organization',
        type: 'error'
      }));
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <BuildingOfficeIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {organization ? 'Edit Organization' : 'Create New Organization'}
              </h1>
              <p className="text-orange-100 mt-1">
                {organization ? 'Update organization settings and configuration' : 'Set up a new organization with custom settings'}
              </p>
            </div>
          </div>
        </div>

        {/* Basic Information Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                <BuildingOfficeIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Organization Information
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Basic details and contact information
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Organization Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organization Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                placeholder="Enter organization name"
              />
            </div>

            {/* Domains */}
            <div>
              <label htmlFor="domains" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Authorized Domains
              </label>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 p-4 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 min-h-[60px]">
                  {formData.domains.map((domain, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700"
                    >
                      {domain}
                      <button
                        type="button"
                        onClick={() => removeDomain(domain)}
                        className="flex-shrink-0 w-4 h-4 rounded-full hover:bg-primary-200 dark:hover:bg-primary-800 focus:outline-none transition-colors duration-200"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    onKeyDown={handleDomainKeyDown}
                    onBlur={() => {
                      if (domainInput.trim()) {
                        addDomain();
                      }
                    }}
                    placeholder={formData.domains.length === 0 ? "Enter domains (press Enter or comma to add)" : "Add another domain..."}
                    className="flex-1 min-w-[200px] border-0 p-0 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-0 text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Add email domains that are authorized for this organization. Press Enter or comma to add multiple domains.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label htmlFor="billing_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="w-4 h-4" />
                    Billing Email
                  </div>
                </label>
                <input
                  type="email"
                  name="billing_email"
                  id="billing_email"
                  value={formData.billing_email}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                  placeholder="billing@company.com"
                />
              </div>

              <div>
                <label htmlFor="technical_contact_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="w-4 h-4" />
                    Technical Contact Email
                  </div>
                </label>
                <input
                  type="email"
                  name="technical_contact_email"
                  id="technical_contact_email"
                  value={formData.technical_contact_email}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                  placeholder="tech@company.com"
                />
              </div>
            </div>

            {/* Super Admin Only Fields */}
            {isSuperAdmin && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div>
                  <label htmlFor="max_users" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maximum Users
                  </label>
                  <input
                    type="number"
                    name="max_users"
                    id="max_users"
                    value={formData.max_users}
                    onChange={handleChange}
                    required
                    min="1"
                    className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                  />
                </div>

                {organization && (
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      name="status"
                      id="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    >
                      <option value="active">Active</option>
                      <option value="disabled">Disabled</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Trial Management - Super Admin */}
        {organization && isSuperAdmin && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                  <ClockIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Trial Management
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure trial access for organization features
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <TrialManagement
                trials={trialInfo}
                onChange={setTrialInfo}
                level="organization"
              />
            </div>
          </div>
        )}

        {/* Trial Status - Org Admin */}
        {organization && isOrgAdmin && (
          (() => {
            const activeTrials = [
              { key: 'web', label: 'Web Trial', color: 'bg-blue-500', icon: '🌐' },
              { key: 'ide', label: 'IDE Trial', color: 'bg-green-500', icon: '💻' },
              { key: 'security', label: 'Security Trial', color: 'bg-red-500', icon: '🔒' },
              { key: 'devops', label: 'DevOps Trial', color: 'bg-yellow-500', icon: '⚙️' }
            ].filter(trial => {
              const trialKey = `${trial.key}_trial` as keyof typeof trialInfo;
              return orgLimits[`${trial.key}_access` as keyof typeof orgLimits] === true && trialInfo[trialKey] === true;
            });

            if (activeTrials.length === 0) return null;

            return (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                      <ClockIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Active Trials
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Current trial access for this organization
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {activeTrials.map(trial => {
                      const expiresKey = `${trial.key}_trial_expires` as keyof typeof trialInfo;
                      const expiryDate = trialInfo[expiresKey] as string | null;
                      
                      const getTrialStatus = (date: string | null) => {
                        if (!date) return { text: 'No expiry set', status: 'warning', daysLeft: null };
                        const expiry = new Date(date);
                        const now = new Date();
                        
                        if (expiry < now) {
                          return { 
                            text: `Expired on ${expiry.toLocaleDateString()}`, 
                            status: 'expired', 
                            daysLeft: null 
                          };
                        }
                        
                        const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                        let status = 'active';
                        if (daysLeft <= 3) status = 'critical';
                        else if (daysLeft <= 7) status = 'warning';
                        
                        return { 
                          text: expiry.toLocaleDateString(), 
                          status, 
                          daysLeft 
                        };
                      };

                      const trialStatus = getTrialStatus(expiryDate);

                      return (
                        <div key={trial.key} className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:shadow-md transition-all duration-200">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${trial.color} flex items-center justify-center text-white text-lg shadow-sm`}>
                                {trial.icon}
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 dark:text-white">
                                  {trial.label}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Organization Level
                                </p>
                              </div>
                            </div>
                            
                            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                              trialStatus.status === 'active' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : trialStatus.status === 'warning'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : trialStatus.status === 'critical'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {trialStatus.status === 'expired' ? 'Expired' : 'Active'}
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">Expires:</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {trialStatus.text}
                              </span>
                            </div>
                            
                            {trialStatus.daysLeft !== null && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Days remaining:</span>
                                <span className={`font-medium ${
                                  trialStatus.status === 'critical' 
                                    ? 'text-orange-600 dark:text-orange-400'
                                    : trialStatus.status === 'warning'
                                    ? 'text-yellow-600 dark:text-yellow-400'
                                    : 'text-green-600 dark:text-green-400'
                                }`}>
                                  {trialStatus.daysLeft} days
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                          Trial Information
                        </h4>
                        <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
                          <li>• Trials provide temporary access to premium features</li>
                          <li>• Organization trials apply to all members</li>
                          <li>• Contact your administrator to extend trials</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()
        )}

        {/* Credit Limits & Controls */}
        {canManageLimits && organization && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                  <CreditCardIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Credit Limits & Controls
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Advanced credit limits and feature access controls
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* Credit Limits */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Credit Limits</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Soft Limit
                    </label>
                    <input
                      type="number"
                      value={orgLimits.credit_soft_limit}
                      onChange={(e) => setOrgLimits({ ...orgLimits, credit_soft_limit: Number(e.target.value) })}
                      className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hard Limit
                    </label>
                    <input
                      type="number"
                      value={orgLimits.credit_hard_limit}
                      onChange={(e) => setOrgLimits({ ...orgLimits, credit_hard_limit: Number(e.target.value) })}
                      className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
                    <input
                      type="checkbox"
                      checked={orgLimits.credit_hard_limit_enabled}
                      onChange={(e) => setOrgLimits({ ...orgLimits, credit_hard_limit_enabled: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Enable hard limit enforcement
                    </span>
                  </label>
                </div>
              </div>

              {/* Credit Pool */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Credit Pool</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pool Type {isOrgAdmin && '(View Only)'}
                    </label>
                    {isSuperAdmin ? (
                      <select
                        value={orgLimits.credit_pool_type}
                        onChange={(e) => setOrgLimits({ ...orgLimits, credit_pool_type: e.target.value as 'monthly' | 'yearly' })}
                        className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    ) : (
                      <div className="block w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                        {orgLimits.credit_pool_type === 'monthly' ? 'Monthly' : 'Yearly'}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pool Amount {isOrgAdmin && '(View Only)'}
                    </label>
                    {isSuperAdmin ? (
                      <input
                        type="number"
                        value={orgLimits.credit_pool_type === 'monthly' ? orgLimits.credit_pool_monthly || 0 : orgLimits.credit_pool_yearly || 0}
                        onChange={(e) => setOrgLimits({ 
                          ...orgLimits, 
                          [orgLimits.credit_pool_type === 'monthly' ? 'credit_pool_monthly' : 'credit_pool_yearly']: Number(e.target.value)
                        })}
                        className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                      />
                    ) : (
                      <div className="block w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                        {(orgLimits.credit_pool_type === 'monthly' ? orgLimits.credit_pool_monthly || 0 : orgLimits.credit_pool_yearly || 0).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Feature Access */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Feature Access</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'security_access', label: 'Security', icon: '🔒', color: 'bg-red-500' },
                    { key: 'devops_access', label: 'DevOps', icon: '⚙️', color: 'bg-yellow-500' },
                    { key: 'ide_access', label: 'IDE', icon: '💻', color: 'bg-green-500' },
                    { key: 'web_access', label: 'Web', icon: '🌐', color: 'bg-blue-500' },
                  ].filter((feature) => {
                    // For super admin, show all features
                    if (isSuperAdmin) return true;
                    
                    // For org admin, only show features they have access to (must be explicitly true)
                    return orgLimits[feature.key as keyof typeof orgLimits] === true;
                  }).map((feature) => (
                    <label key={feature.key} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200">
                      <div className={`w-8 h-8 rounded-lg ${feature.color} flex items-center justify-center text-white text-sm`}>
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <input
                          type="checkbox"
                          checked={orgLimits[feature.key as keyof typeof orgLimits] as boolean}
                          onChange={(e) => setOrgLimits({ 
                            ...orgLimits, 
                            [feature.key]: e.target.checked 
                          })}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                          {feature.label}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Auto Top-up */}
              {(isSuperAdmin || (isOrgAdmin && orgLimits.auto_topup_enabled === true)) && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Auto Top-up</h4>
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
                      <input
                        type="checkbox"
                        checked={orgLimits.auto_topup_enabled}
                        onChange={(e) => setOrgLimits({ ...orgLimits, auto_topup_enabled: e.target.checked })}
                        disabled={isOrgAdmin}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable automatic top-up {isOrgAdmin && '(View Only)'}
                      </span>
                    </label>
                    
                    {orgLimits.auto_topup_enabled && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 ml-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Top-up Amount {isOrgAdmin && '(View Only)'}
                          </label>
                          {isSuperAdmin ? (
                            <input
                              type="number"
                              value={orgLimits.auto_topup_amount}
                              onChange={(e) => setOrgLimits({ ...orgLimits, auto_topup_amount: Number(e.target.value) })}
                              className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                            />
                          ) : (
                            <div className="block w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                              {orgLimits.auto_topup_amount?.toLocaleString() || 0}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Trigger Threshold (%)
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            value={orgLimits.auto_topup_threshold}
                            onChange={(e) => setOrgLimits({ ...orgLimits, auto_topup_threshold: Number(e.target.value) })}
                            className="block w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notification Thresholds */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Notification Thresholds</h4>
                <div className="space-y-3">
                  {[
                    { key: 'notification_threshold_80', label: 'Notify at 80% usage', color: 'text-green-600' },
                    { key: 'notification_threshold_90', label: 'Notify at 90% usage', color: 'text-yellow-600' },
                    { key: 'notification_threshold_95', label: 'Notify at 95% usage', color: 'text-red-600' },
                  ].map((threshold) => (
                    <label key={threshold.key} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200">
                      <input
                        type="checkbox"
                        checked={orgLimits[threshold.key as keyof typeof orgLimits] as boolean}
                        onChange={(e) => setOrgLimits({ 
                          ...orgLimits, 
                          [threshold.key]: e.target.checked 
                        })}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className={`text-sm font-medium ${threshold.color} dark:text-gray-300`}>
                        {threshold.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* App Features Management - Super Admin Only */}
        {organization && isSuperAdmin && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                  <CubeIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    App Features Management
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Control feature flags for this organization. Changes will apply to all users in this organization.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {loadingFeatures ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading features...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                          Hierarchical Feature Management
                        </h4>
                        <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
                          <li>• Organization features automatically apply to ALL users in this organization</li>
                          <li>• Adding features here will enable them for every user</li>
                          <li>• Removing features here will disable them for every user</li>
                          <li>• Individual user features can be managed separately in the Users section</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {availableFeatures.map((feature) => {
                      const featureInfo = FEATURE_DEFINITIONS[feature];
                      const isEnabled = orgAppFeatures.includes(feature);
                      
                      return (
                        <div key={feature} className="flex items-start space-x-3 p-4 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                          <input
                            type="checkbox"
                            id={`org-feature-${feature}`}
                            checked={isEnabled}
                            onChange={(e) => {
                              const newFeatures = e.target.checked
                                ? [...orgAppFeatures, feature]
                                : orgAppFeatures.filter(f => f !== feature);
                              
                              setOrgAppFeatures(newFeatures);
                            }}
                            disabled={loadingFeatures}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                          />
                          <div className="flex-1">
                            <label htmlFor={`org-feature-${feature}`} className="text-sm font-medium cursor-pointer text-gray-900 dark:text-white">
                              {featureInfo?.displayName || feature}
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {featureInfo?.description || 'No description available'}
                            </p>
                          </div>
                          {isEnabled && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <CheckIcon className="w-3 h-3 mr-1" />
                              Enabled
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {orgAppFeatures.length > 0 && (
                    <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                      <h4 className="font-medium mb-2 text-green-900 dark:text-green-200">
                        Active Features ({orgAppFeatures.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {orgAppFeatures.map((feature) => (
                          <span key={feature} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {FEATURE_DEFINITIONS[feature]?.displayName || feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-lg transition-all duration-200"
          >
            {organization ? 'Update Organization' : 'Create Organization'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationForm;
